import DOMPurify from "dompurify";
import React, { forwardRef } from "react";
import { Svg, SvgSize } from "../../../../Shared/Assets/Svg/React";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonType
} from "../../../../Shared/Buttons/React";
import { useProvetStatic, useTranslationContext } from "../../context";
import { CareUnit, ProvetFormData } from "../../types";
import { formatAppointmentDate, formatAppointmentTime } from "../../utilities";

interface ProvetConfirmationViewProps {
  sessionId: string;
  formData?: ProvetFormData;
  selectedCareUnit?: CareUnit;
  bookingInformation?: string;
  bookingId?: number;
  onResetBooking: () => void;
  onCancelBooking: () => void;
}

/**
 * Displays the final confirmation screen after a successful booking.
 * Shows booking details and provides options to make another appointment or visit the clinic.
 */
export const ProvetConfirmationView = forwardRef<
  HTMLDivElement,
  ProvetConfirmationViewProps
>(
  (
    {
      sessionId,
      formData,
      selectedCareUnit,
      bookingInformation,
      bookingId,
      onResetBooking,
      onCancelBooking
    },
    ref
  ) => {
    const translationContext = useTranslationContext();
    // The site language, so that the date is not shown in the browser's or default language
    const { language } = useProvetStatic();
    const translations = translationContext?.translations;

    return (
      <div
        ref={ref}
        className="font-guardian text-t-md leading-lg font-normal text-dark-blue sm:py-xl md:text-t-md md:leading-xxl"
        id={`${sessionId}_Confirmed`}
      >
        {/* Success Header */}
        <div className="flex gap-md items-baseline mb-lg font-guardian text-t-xl font-t-semi-bold leading-xxl md:mb-2xl md:text-h2 md:leading-3xl">
          <Svg
            href="/svg/system/check.svg"
            size={SvgSize.XXL}
            additionalClass={"relative top-xs text-4xl text-success-200"}
          />
          {translations?.bookingConfirmed}
        </div>

        {/* Welcome Message */}
        <div className="mb-lg font-guardian text-h1 font-t-semi-bold">
          {translations?.welcomeTo} {formData?.selectedDepartment?.name}
        </div>

        {/* Booking Details Card */}
        <div className="grid p-lg mb-2xl gap-y-xxs font-guardian text-t-sm font-normal leading-md bg-neutral-100 rounded-sm md:text-t-md md:leading-lg">
          <span className="mb-xs font-guardian text-distinct-xs font-distinct text-dark-blue uppercase md:text-distinct-sm">
            {translations?.yourBooking}
          </span>
          {/* Appointment Date and Time */}
          <span className="mb-xxs font-guardian text-t-xl sm:text-h2 font-t-semi-bold leading-xxl sm:leading-3xl tracking-xs text-dark-blue inline-flex items-center gap-xs">
            {formData?.selectedAppointmentTime?.availableTime && (
              <>
                {formatAppointmentDate(
                  formData.selectedAppointmentTime.availableTime,
                  language
                )}
                <span className="inline-flex w-xxs h-xxs bg-t-neutral-200 rounded-full">
                  {" "}
                </span>
                {formatAppointmentTime(formData.selectedAppointmentTime.availableTime)}
                {formData.selectedAppointmentTime.selectedVeterinarianName && (
                  <>
                    <span className="inline-flex w-xxs h-xxs bg-t-neutral-200 rounded-full">
                      {" "}
                    </span>
                    {formData.selectedAppointmentTime.selectedVeterinarianName}
                  </>
                )}
              </>
            )}
          </span>
          {/* Treatment */}
          <p className="text-t-neutral-800">{formData?.selectedReason?.name}</p>
          {/* Department */}
          <p className="text-t-neutral-800">{formData?.selectedDepartment?.name}</p>
          {/* Client and Patient */}
          <p className="inline-flex items-center gap-xs text-t-neutral-800">
            {formData?.contactDetails?.client.firstName}{" "}
            {formData?.contactDetails?.client.lastName}
            <span className="inline-flex w-xxs h-xxs bg-t-neutral-200 rounded-full">
              {" "}
            </span>
            {formData?.contactDetails?.patient.name}
          </p>
          {/* Cancel booking */}
          {bookingId && (
            <Button
              type={ButtonType.Button}
              size={ButtonSize.LG}
              color={ButtonColor.Neutral}
              onClick={onCancelBooking}
              additionalClass={"mt-xl justify-self-start"}
              data-online-booking-action="cancelBooking"
            >
              {translations?.cancelBooking}
            </Button>
          )}
        </div>

        {/* Additional Booking Information */}
        {bookingInformation && (
          <div className="mb-2xl text-t-neutral-800">
            <div
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bookingInformation) }}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-sm sm:flex-row">
          <Button
            type={ButtonType.Button}
            size={ButtonSize.LG}
            color={ButtonColor.Turquoise}
            onClick={onResetBooking}
            data-online-booking-action="resetBooking"
          >
            {translations?.makeAnotherAppointment}
          </Button>
          {selectedCareUnit?.url && (
            <Button
              href={selectedCareUnit.url}
              size={ButtonSize.LG}
              color={ButtonColor.OutlineNeutral}
              newWindowScreenReaderText={translations?.newWindow}
              data-online-booking-action="visitClinic"
            >
              {translations?.visitClinic}
            </Button>
          )}
        </div>
      </div>
    );
  }
);

ProvetConfirmationView.displayName = "ProvetConfirmationView";
